const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Set only while a super admin is using "View as Teacher/Student" — see
// AppContext.js. Tab-scoped (sessionStorage) and never persisted beyond
// that, so a refresh always comes back as the real admin.
const impersonationHeader = () => {
  const id = sessionStorage.getItem('impersonatingId');
  if (!id) return {};
  return { 'X-Impersonate-As': id, 'X-Impersonate-Role': sessionStorage.getItem('impersonatingRole') || 'teacher' };
};

const request = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...impersonationHeader(),
      ...options.headers,
    },
  });

  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Request failed');
  return data;
};

export const authService = {
  login: (identifier, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ identifier, password }) }),
  google: (credential) => request('/auth/google', { method: 'POST', body: JSON.stringify({ credential }) }),
  me: () => request('/auth/me'),
};

export const studentService = {
  list: () => request('/students'),
  get: (id) => request(`/students/${id}`),
  enableLogin: (id) => request(`/students/${id}/credentials`, { method: 'POST', body: JSON.stringify({}) }),
  revokeLogin: (id) => request(`/students/${id}/credentials`, { method: 'DELETE' }),
  updateEmail: (id, email) => request(`/students/${id}`, { method: 'PATCH', body: JSON.stringify({ email }) }),
  saveNote: (id, comment) => request(`/students/${id}/note`, { method: 'PUT', body: JSON.stringify({ comment }) }),
};

export const staffService = {
  list: () => request('/staff'),
  create: (name, email, password, role, teachingLanguage) =>
    request('/staff', { method: 'POST', body: JSON.stringify({ name, email, password, role, teachingLanguage }) }),
  update: (id, updates) => request(`/staff/${id}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  remove: (id) => request(`/staff/${id}`, { method: 'DELETE' }),
};

export const batchService = {
  list: () => request('/batches'),
  mine: () => request('/batches/mine'),
  create: (name, subject, startDate, teacherId, isExternal) =>
    request('/batches', { method: 'POST', body: JSON.stringify({ name, subject, startDate, teacherId, isExternal }) }),
  update: (batchId, updates) =>
    request(`/batches/${batchId}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  remove: (batchId) => request(`/batches/${batchId}`, { method: 'DELETE' }),
  end: (batchId) => request(`/batches/${batchId}/end`, { method: 'POST' }),
  setTiming: (batchId, timing) =>
    request(`/batches/${batchId}/timing`, { method: 'PATCH', body: JSON.stringify(timing) }),
  roster: (batchId) => request(`/batches/${batchId}/roster`),
  enroll: (batchId, studentId) =>
    request(`/batches/${batchId}/enroll`, { method: 'POST', body: JSON.stringify({ studentId }) }),
  unenroll: (batchId, studentId) =>
    request(`/batches/${batchId}/enroll/${studentId}`, { method: 'DELETE' }),
};

export const attendanceService = {
  mark: (batchId, studentId, date, status) =>
    request('/attendance', { method: 'POST', body: JSON.stringify({ batchId, studentId, date, status }) }),
  forBatch: (batchId, date) =>
    request(`/attendance/batch/${batchId}${date ? `?date=${date}` : ''}`),
  mine: () => request('/attendance/mine'),
  checkin: (batchId) => request('/attendance/checkin', { method: 'POST', body: JSON.stringify({ batchId }) }),
  unmark: (id) => request(`/attendance/${id}`, { method: 'DELETE' }),
};

export const classNoteService = {
  listForBatch: (batchId) => request(`/class-notes/batch/${batchId}`),
  mine: () => request('/class-notes/mine'),
  setNote: (batchId, date, note) =>
    request(`/class-notes/batch/${batchId}/date/${date}`, { method: 'PUT', body: JSON.stringify({ note }) }),
};

export const materialService = {
  listForBatch: (batchId) => request(`/materials/batch/${batchId}`),
  listCommon: () => request('/materials/common'),
  mine: () => request('/materials/mine'),
  _upload: async (endpoint, fields, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });
    formData.append('file', file);
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...impersonationHeader() },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Upload failed');
    return data;
  },
  upload: (batchId, date, title, file) =>
    materialService._upload('/materials', { batchId, date, title }, file),
  uploadCommon: (subject, title, file) =>
    materialService._upload('/materials/common', { subject, title }, file),
  remove: (id) => request(`/materials/${id}`, { method: 'DELETE' }),
  _fetchBlob: async (id) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/materials/${id}/download`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...impersonationHeader() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to fetch the PDF');
    }
    return res.blob();
  },
  download: async (id, filename) => {
    const blob = await materialService._fetchBlob(id);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  // A real HTTP URL the browser navigates to directly (via window.open),
  // so it renders in the browser's native PDF viewer with no extra JS —
  // avoids blob: URL cross-tab restrictions entirely. The endpoint can't
  // read an Authorization header from a plain navigation, so the token
  // rides along as a query param instead (auth.js accepts either).
  view: (id) => {
    const token = localStorage.getItem('token');
    const impersonatingId = sessionStorage.getItem('impersonatingId');
    const impersonateParam = impersonatingId
      ? `&impersonateAs=${encodeURIComponent(impersonatingId)}&impersonateRole=${encodeURIComponent(sessionStorage.getItem('impersonatingRole') || 'teacher')}`
      : '';
    const url = `${API_BASE}/materials/${id}/download?inline=1&token=${encodeURIComponent(token)}${impersonateParam}`;
    const newTab = window.open(url, '_blank');
    if (!newTab) {
      throw new Error('Pop-up blocked — allow pop-ups for this site to view the PDF');
    }
  },
};

export const examService = {
  // Staff
  list: () => request('/exams'),
  get: (id) => request(`/exams/${id}`),
  create: (title, level, durationMinutes) =>
    request('/exams', { method: 'POST', body: JSON.stringify({ title, level, durationMinutes }) }),
  update: (id, updates) => request(`/exams/${id}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  remove: (id) => request(`/exams/${id}`, { method: 'DELETE' }),
  addQuestion: (examId, question) =>
    request(`/exams/${examId}/questions`, { method: 'POST', body: JSON.stringify(question) }),
  updateQuestion: (examId, questionId, question) =>
    request(`/exams/${examId}/questions/${questionId}`, { method: 'PATCH', body: JSON.stringify(question) }),
  removeQuestion: (examId, questionId) =>
    request(`/exams/${examId}/questions/${questionId}`, { method: 'DELETE' }),
  // A listening question always carries an audio file, so it goes
  // through multipart instead of the plain-JSON question routes above.
  _uploadListening: async (method, endpoint, { text, options, correctIndex }, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('text', text);
    options.forEach((o) => formData.append('options', o));
    formData.append('correctIndex', correctIndex);
    if (file) formData.append('file', file);
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...impersonationHeader() },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Request failed');
    return data;
  },
  addListeningQuestion: (examId, question, file) =>
    examService._uploadListening('POST', `/exams/${examId}/questions/listening`, question, file),
  updateListeningQuestion: (examId, questionId, question, file) =>
    examService._uploadListening('PATCH', `/exams/${examId}/questions/${questionId}/listening`, question, file),
  // Same authenticated-URL-via-query-param approach as materialService.view()
  // — an <audio> tag's src is a plain navigation/fetch with no header.
  audioUrl: (examId, questionId) => {
    const token = localStorage.getItem('token');
    const impersonatingId = sessionStorage.getItem('impersonatingId');
    const impersonateParam = impersonatingId
      ? `&impersonateAs=${encodeURIComponent(impersonatingId)}&impersonateRole=${encodeURIComponent(sessionStorage.getItem('impersonatingRole') || 'teacher')}`
      : '';
    return `${API_BASE}/exams/${examId}/questions/${questionId}/audio?token=${encodeURIComponent(token)}${impersonateParam}`;
  },
  publish: (id) => request(`/exams/${id}/publish`, { method: 'POST' }),
  close: (id) => request(`/exams/${id}/close`, { method: 'POST' }),
  results: (id) => request(`/exams/${id}/results`),
  gradeAttempt: (examId, attemptId, scores) =>
    request(`/exams/${examId}/attempts/${attemptId}/grade`, { method: 'POST', body: JSON.stringify({ scores }) }),
  // Student
  mine: () => request('/exams/mine'),
  start: (id) => request(`/exams/${id}/start`, { method: 'POST' }),
  submit: (id, answers) =>
    request(`/exams/${id}/submit`, { method: 'POST', body: JSON.stringify({ answers }) }),
};

export const backupService = {
  stats: () => request('/backup/stats'),
  download: async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/backup/export`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...impersonationHeader() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Backup export failed');
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lms-backup-${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  restore: async (file, restoreFiles) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('backup', file);
    formData.append('confirm', 'RESTORE');
    formData.append('restoreFiles', restoreFiles ? 'true' : 'false');
    const res = await fetch(`${API_BASE}/backup/restore`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...impersonationHeader() },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Restore failed');
    return data;
  },
};
